import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { formatApiError } from '../utils/errorMessage';
import { formatShortDate } from '../features/dashboard/calendarDate';
import BalanceDrill from '../features/drilldown/BalanceDrill';

// The records behind a dashboard or report figure. The total at the top is
// computed by the same function as the figure that was clicked.
const LABELS = { income: 'Income', expenses: 'Expenses', net: 'Net income' };
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v) || 0);

export function drilldownParams(search) {
  const p = new URLSearchParams(search);
  const out = {};
  for (const k of ['metric', 'start_date', 'end_date', 'business_id', 'category_id', 'account_id', 'transaction_type']) {
    if (p.get(k)) out[k] = p.get(k);
  }
  return out;
}

function Rows({ rows, amountKey }) {
  return (
    <table className="min-w-full text-sm">
      <thead><tr className="text-left text-gray-600"><th className="py-2 pr-4">Date</th><th className="pr-4">Description</th><th className="pr-4">Account</th><th className="pr-4">Category</th><th className="pr-4">Type</th><th className="text-right">Amount</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.id}-${r.split_id || 0}`} className="border-t">
            <td className="py-1 pr-4 whitespace-nowrap">{formatShortDate(r.transaction_date)}</td>
            <td className="pr-4">{r.description}</td>
            <td className="pr-4">{r.account_name || ''}</td>
            <td className="pr-4">{r.category_name}</td>
            <td className="pr-4 capitalize">{r.transaction_type}</td>
            <td className="text-right whitespace-nowrap">{money(r[amountKey])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function Drilldown() {
  const { search } = useLocation();
  return new URLSearchParams(search).get('kind') ? <BalanceDrill search={search} /> : <TransactionDrill search={search} />;
}

// Transactions behind an income / expenses / net figure.
function TransactionDrill({ search }) {
  const [showExcluded, setShowExcluded] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setError('');
    api.get('/dashboard/drilldown', { params: { ...drilldownParams(search), include_excluded: showExcluded ? 'true' : undefined } })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(formatApiError(e, 'Could not load these records')); });
    return () => { live = false; };
  }, [search, showExcluded]);
  const f = drilldownParams(search);
  return (
    <div className="space-y-4">
      <Link to="/dashboard" className="text-sm text-blue-600">Back to dashboard</Link>
      <h2 className="text-2xl font-bold text-gray-900">{LABELS[f.metric] || 'Records'} {f.start_date} to {f.end_date}</h2>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {data && (
        <>
          <div className="bg-white rounded-lg shadow p-4" data-testid="drilldown-total">
            <div className="text-sm text-gray-600">Total ({data.count} records{data.filters.category_id ? `, category ${data.rows[0]?.category_name || data.filters.category_id}` : ''})</div>
            <div className="text-2xl font-bold">{money(data.total)}</div>
            {data.truncated && <div className="text-xs text-gray-500">Showing the newest {data.rows.length}; the total covers all {data.count}.</div>}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showExcluded} onChange={(e) => setShowExcluded(e.target.checked)} />
            Show excluded transfers
          </label>
          <div className="bg-white rounded-lg shadow p-4 overflow-x-auto"><Rows rows={data.rows} amountKey="counted_amount" /></div>
          {showExcluded && data.excluded && (
            <div className="bg-gray-50 rounded-lg shadow p-4 overflow-x-auto" data-testid="drilldown-excluded">
              <div className="text-sm text-gray-700 mb-2">Not counted: {data.excluded.count} records ({money(data.excluded.total)}). {data.excluded.reason}</div>
              <Rows rows={data.excluded.rows} amountKey="amount" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
